<template>
  <li :class="classes">
    <span :class="classesStyle">
      <slot
        v-if="$slots.label"
        name="label"
      />

      <template v-else>
        {{ label }}
      </template>

      <context-tag
        v-if="tag"
        :color="tag.color"
        :icon="tag.icon"
      >
        {{ tag.label }}
      </context-tag>
    </span>

    <span
      v-if="!valueless"
      class="cordel-information-line-value"
    >
      <slot v-if="$slots.default" />

      <span
        v-else
        class="cordel-information-line-value-raw"
      >
        {{ resolved }}

        <form-button
          v-if="copiable && !empty"
          :icon="copied ? 'check' : 'copy'"
          :disabled="copied"
          class="cordel-information-line-copy"
          size="small"
          @click="copy"
        />
      </span>
    </span>
  </li>
</template>

<script>
  import ContextTag from '@cordel/components/Context/Tag.vue';
  import FormButton from '@cordel/components/Form/Button.vue';

  const BLANK = 'Em branco';

  export default {
    name: 'CordelInformationLine',
    components: {
      ContextTag,
      FormButton,
    },
    props: {
      label: {
        type: String,
        default: '',
      },
      otherlabel: {
        type: String,
        default: '',
      },
      value: {
        type: [String, Number, Boolean],
        default: null,
      },
      valueless: {
        type: Boolean,
        default: false,
      },
      fields: {
        type: Array,
        default() {
          return [];
        },
      },
      field: {
        type: Object,
        default() {
          return {};
        },
      },
      tag: {
        type: Object,
        default() {
          return {};
        },
      },
      index: {
        type: Number,
        default: null,
      },
      copiable: {
        type: Boolean,
        default: true,
      },
      outdated: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        copied: false,
        timeout: null,
      };
    },
    computed: {
      empty() {
        return ['', BLANK, null, undefined].includes(this.value);
      },
      resolved() {
        if (this.empty) {
          return BLANK;
        }

        if (typeof this.value === 'boolean') {
          return this.value ? 'Sim' : 'Não';
        }

        if (this.field.options && this.field.options.length) {
          const option = this.field.options.find(o => o.value === this.value);

          if (option) {
            return option.label || option.otherlabel;
          }
        }

        return this.value;
      },
      separator() {
        if (this.field.separator) {
          return true;
        }

        return (this.index && typeof this.index === 'number' && this.fields.length && this.fields[this.index].group !== this.fields[this.index - 1].group);
      },

      classes() {
        const classes = ['cordel-information-line'];

        if (this.separator) {
          classes.push('variant--separator');
        }

        if (this.empty) {
          classes.push('variant--empty');
        }

        if (Object.keys(this.fields).length > 2 && this.index) {
          classes.push('variant--bordered');
        }

        if (this.$slots.default) {
          classes.push('variant--centered');
        }

        if (this.field.labelClass === 'outdated-label') {
          classes.push('outdated-label-class');
        }

        if (this.separator && this.field.labelClass === 'outdated-label') {
          classes.push('variant--separator-outdated');
        }

        return classes;
      },

      classesStyle() {
        const classesLabel = ['cordel-information-line-label'];

        if (this.outdated) {
          classesLabel.push('outdated-label-class');
        }

        return classesLabel;
      },
    },
    methods: {
      copy() {
        if (!navigator.clipboard) {
          console.error('Clipboard API not supported in this browser');
          return;
        }
        this.copied = true;
        navigator.clipboard.writeText(this.resolved);

        clearTimeout(this.timeout);
        this.timeout = setTimeout(this.clear, 1000);
      },
      clear() {
        this.copied = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-information-line {
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    line-height: var(--cordel-line-height-m);
    margin: var(--cordel-spacing-xxxs) 0 0;

    &:first-child {
      margin: 0;
    }

    &.variant--centered {
      align-items: center;
    }

    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-font-size-s);
    }
  }

  .cordel-information-line-otherlabel {
    margin-left: var(--cordel-spacing-s);
  }

  .cordel-information-line-value {
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: row;
    font-weight: var(--cordel-font-weight-bold);
    justify-content: flex-end;
    margin-left: var(--cordel-spacing-giga);
  }

  .cordel-information-line-value-raw {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    text-align: right;

    .variant--empty & {
      color: var(--cordel-color-neutral-primary);
    }
  }

  .cordel-information-line-copy {
    --button-line-height: calc(var(--cordel-line-height-s) * var(--cordel-font-size-s));
    --button-height: calc(var(--button-line-height) + var(--cordel-spacing-micro) * 2);
    --line-line-height: calc(var(--cordel-line-height-s) * var(--cordel-font-size-m));

    display: none;
    margin: calc((var(--button-height) - var(--line-line-height)) / -2) 0;
    margin-left: var(--cordel-spacing-nano);

    .cordel-information-line:hover & {
      display: block;
    }
  }

  .variant--bordered {
    border-top: var(--cordel-border-width-soft) solid var(--cordel-color-neutral-light);
    margin: var(--cordel-spacing-nano) 0;
    padding: var(--cordel-spacing-nano) 0 0;
  }

  .variant--separator {
    border-top: var(--cordel-border-width-soft) solid var(--cordel-color-neutral-primary);
    margin: var(--cordel-spacing-xl) 0 0;
    padding: var(--cordel-spacing-xl) 0 0;
  }

  .variant--separator-outdated {
    border-top: var(--cordel-border-width-soft) solid var(--cordel-color-neutral-primary);
    margin: var(--cordel-spacing-xl) 0 0;
    padding: var(--cordel-spacing-xl) 0 0;
  }

  .outdated-label-class {
    color: var(--cordel-color-neutral-primary);
  }
</style>
